import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

/** The page's column: 16px gutters on phones, capped and centered on wide screens. */
export function Container({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn("mx-auto w-full max-w-6xl px-4 sm:px-6", className)}>{children}</div>;
}

/**
 * The band behind a section, so the page alternates. Both follow the theme:
 * light pages stay light and dark pages dark, no inverted bands.
 * - `plain`: the page background
 * - `tinted`: a faint secondary wash
 * - `tintedToClosing`: the same wash, fading in at the top only, for the band
 *   right above the closing block (`CLOSING_BAND_JOINED`), so the two read as
 *   one surface with no dip between them
 * - `none`: no band of its own; the section sits on its parent's surface
 */
export type BandTone = "plain" | "tinted" | "tintedToClosing" | "none";

/**
 * A tinted band has no hard edges: the wash fades in over its first 6rem and
 * out over its last, so sections flow into each other without divider lines.
 */
export const TINTED_BAND =
  "before:pointer-events-none before:absolute before:inset-0 before:-z-20 before:bg-secondary/50 before:content-[''] before:[mask-image:linear-gradient(to_bottom,transparent,#000_6rem,#000_calc(100%-6rem),transparent)] dark:before:bg-secondary/25";

/**
 * The closing surface under the final call to action and the footer: the same
 * wash, fading in at the top only, so the two read as one block down to the
 * page's end.
 */
export const CLOSING_BAND =
  "before:pointer-events-none before:absolute before:inset-0 before:-z-20 before:bg-secondary/50 before:content-[''] before:[mask-image:linear-gradient(to_bottom,transparent,#000_10rem)] dark:before:bg-secondary/25";

/** A tinted band that runs on into the closing block: fades in at the top only. */
export const TINTED_TO_CLOSING_BAND =
  "before:pointer-events-none before:absolute before:inset-0 before:-z-20 before:bg-secondary/50 before:content-[''] before:[mask-image:linear-gradient(to_bottom,transparent,#000_6rem)] dark:before:bg-secondary/25";

/**
 * The closing surface when the band above it is tinted (`tintedToClosing`):
 * the wash from the very top, so there is no seam where the two meet.
 */
export const CLOSING_BAND_JOINED =
  "before:pointer-events-none before:absolute before:inset-0 before:-z-20 before:bg-secondary/50 before:content-[''] dark:before:bg-secondary/25";

const BAND: Record<BandTone, string> = {
  plain: "bg-background",
  tinted: TINTED_BAND,
  tintedToClosing: TINTED_TO_CLOSING_BAND,
  none: "",
};

/**
 * A landing section: a real `<section>` with an anchor id that clears the
 * sticky nav, on a band, optionally with the contour field and a green glow.
 */
export function Section({
  id,
  labelledBy,
  tone = "plain",
  contours,
  glow,
  className,
  children,
}: {
  id: string;
  labelledBy: string;
  tone?: BandTone;
  /** The contour variation behind this section; omit for none. */
  contours?: ContourVariant;
  /** A soft green glow behind the content. */
  glow?: "top" | "center" | "bottom";
  className?: string;
  children: ReactNode;
}) {
  return (
    <section
      id={id}
      aria-labelledby={labelledBy}
      className={cn(
        // Sections are all below the fold: skip their rendering work until they near the viewport.
        "relative isolate scroll-mt-28 overflow-hidden py-20 [contain-intrinsic-size:auto_900px] [content-visibility:auto] sm:py-28 md:scroll-mt-20",
        BAND[tone],
        className
      )}
    >
      {glow && <Glow at={glow} />}
      {contours && <ContourField variant={contours} />}
      {children}
    </section>
  );
}

/**
 * The contour lines ("vrstevnice") behind every second section, each in its
 * own subtle variation, so the motif recurs without repeating: a texture
 * (the app's field or a second seeded one, `contours-b.svg`), a size, and a
 * soft mask that decides where the lines show and where they fade out.
 */
export type ContourVariant = "hero" | "frame" | "side" | "flank" | "field" | "column" | "closing";

const TEXTURE = {
  a: "url(/textures/contours.svg)",
  b: "url(/textures/contours-b.svg)",
} as const;

const VARIANT: Record<
  ContourVariant,
  {
    texture: keyof typeof TEXTURE;
    area: string;
    size: string;
    position: string;
    shape: string;
    strength: string;
  }
> = {
  // The hero: the field over the whole first screen, fading out at its foot.
  hero: {
    texture: "a",
    area: "inset-0",
    size: "max(1600px, 100%) auto",
    position: "center top",
    shape: "linear-gradient(to bottom, #000 35%, transparent)",
    strength: "opacity-[0.2] dark:opacity-[0.11]",
  },
  // Around the app window: a ring the window sits in.
  frame: {
    texture: "b",
    area: "inset-0",
    size: "max(1500px, 105%) auto",
    position: "center",
    shape: "radial-gradient(ellipse 80% 70% at 50% 60%, #000 35%, transparent 75%)",
    strength: "opacity-[0.12] dark:opacity-[0.09]",
  },
  // From the left edge, behind the text column.
  side: {
    texture: "a",
    area: "inset-0",
    size: "1300px auto",
    position: "left center",
    shape: "radial-gradient(ellipse 65% 85% at 0% 50%, #000 30%, transparent 75%)",
    strength: "opacity-[0.13] dark:opacity-[0.08]",
  },
  // From the right edge, behind a picture on the right (the accountants'
  // panel): the mirror of `side`, on the second texture.
  flank: {
    texture: "b",
    area: "inset-0",
    size: "1400px auto",
    position: "right center",
    shape: "radial-gradient(ellipse 65% 85% at 100% 50%, #000 30%, transparent 75%)",
    strength: "opacity-[0.12] dark:opacity-[0.09]",
  },
  // Centred behind the plan cards.
  field: {
    texture: "a",
    area: "inset-0",
    size: "max(1600px, 110%) auto",
    position: "center",
    shape: "radial-gradient(ellipse 90% 75% at 50% 55%, #000 35%, transparent 80%)",
    strength: "opacity-[0.12] dark:opacity-[0.09]",
  },
  // Behind the FAQ heading column, wrapping round the questions. A fixed box
  // pinned to the top (about the closed FAQ's height), not the whole section:
  // opening an answer makes the section taller, and a field sized to it would
  // slide and stretch under the reader.
  column: {
    texture: "a",
    area: "inset-x-0 top-0 h-[52rem]",
    size: "max(1500px, 100%) auto",
    position: "left center",
    shape: "radial-gradient(ellipse 70% 62% at 18% 50%, #000 35%, transparent 80%)",
    strength: "opacity-[0.18] dark:opacity-[0.1]",
  },
  // The closing block: rising from the page's bottom edge through the footer
  // and fading out below the final call to action's buttons.
  closing: {
    texture: "b",
    area: "inset-0",
    size: "max(1600px, 100%) auto",
    position: "center bottom",
    shape: "linear-gradient(to top, #000 20%, transparent 70%)",
    strength: "opacity-[0.18] dark:opacity-[0.1]",
  },
};

export function ContourField({
  variant,
  className,
}: {
  variant: ContourVariant;
  className?: string;
}) {
  const v = VARIANT[variant];
  return (
    <div
      aria-hidden="true"
      className={cn(
        "pointer-events-none absolute -z-10 text-[hsl(var(--action))] dark:text-primary",
        v.area,
        v.strength,
        className
      )}
      style={{ WebkitMaskImage: v.shape, maskImage: v.shape }}
    >
      <div
        className="h-full w-full bg-current"
        style={{
          WebkitMaskImage: TEXTURE[v.texture],
          maskImage: TEXTURE[v.texture],
          WebkitMaskSize: v.size,
          maskSize: v.size,
          WebkitMaskPosition: v.position,
          maskPosition: v.position,
          WebkitMaskRepeat: "no-repeat",
          maskRepeat: "no-repeat",
        }}
      />
    </div>
  );
}

/** A soft radial green glow, the old landing's ambient light. Decorative. */
export function Glow({ at, className }: { at: "top" | "center" | "bottom"; className?: string }) {
  const y = at === "top" ? "0%" : at === "center" ? "50%" : "100%";
  return (
    <div
      aria-hidden="true"
      className={cn(
        "pointer-events-none absolute inset-0 -z-10 [--glow:var(--action)] dark:[--glow:var(--primary)]",
        className
      )}
      style={{
        background: `radial-gradient(ellipse 60% 55% at 50% ${y}, hsl(var(--glow) / 0.16), transparent 70%)`,
      }}
    />
  );
}

/** The small green line above a heading. */
export function Eyebrow({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <p
      className={cn(
        "text-[13px] font-semibold uppercase tracking-[0.08em] text-[hsl(var(--action))] dark:text-primary",
        className
      )}
    >
      {children}
    </p>
  );
}

/** The one h2 of a section and its one sentence. */
export function SectionHeading({
  id,
  eyebrow,
  title,
  lead,
  align = "left",
  className,
}: {
  id: string;
  eyebrow?: string;
  title: string;
  lead?: string;
  align?: "left" | "center";
  className?: string;
}) {
  return (
    <div className={cn("max-w-2xl", align === "center" && "mx-auto text-center", className)}>
      {eyebrow && <Eyebrow className="mb-3">{eyebrow}</Eyebrow>}
      <h2
        id={id}
        className="text-[1.75rem] font-semibold leading-[1.15] tracking-tight sm:text-4xl sm:leading-[1.1]"
      >
        {title}
      </h2>
      {lead && (
        <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">{lead}</p>
      )}
    </div>
  );
}

/**
 * A product mockup's frame: the app's card with the elevation public pages
 * allow (soft shadow, faint ring), so it reads as a screen, not a text box.
 */
export function MockFrame({
  className,
  children,
  label,
}: {
  className?: string;
  children: ReactNode;
  /** What the mockup shows, for screen readers; the mockup itself is hidden. */
  label: string;
}) {
  return (
    <figure
      className={cn(
        "relative rounded-xl border bg-card text-card-foreground shadow-[0_1px_2px_rgb(0_0_0/0.04),0_12px_32px_-12px_rgb(0_0_0/0.18)] dark:shadow-[0_1px_0_rgb(255_255_255/0.04)_inset,0_16px_40px_-16px_rgb(0_0_0/0.7)]",
        className
      )}
    >
      <figcaption className="sr-only">{label}</figcaption>
      <div aria-hidden="true" className="h-full">
        {children}
      </div>
    </figure>
  );
}

/** One supporting line under a mockup. */
export function MockCaption({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <p className={cn("mt-4 text-sm leading-relaxed text-muted-foreground", className)}>
      {children}
    </p>
  );
}

const ctaBase =
  "inline-flex h-11 items-center justify-center whitespace-nowrap rounded-lg px-5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--action))] focus-visible:ring-offset-2 focus-visible:ring-offset-background dark:focus-visible:ring-primary";

export const CTA_STYLES = {
  primary:
    "bg-[hsl(var(--action))] text-[hsl(var(--action-foreground))] hover:bg-[hsl(var(--action)/0.9)] dark:bg-primary dark:text-primary-foreground dark:hover:bg-primary/90 dark:shadow-[0_0_24px_-6px_hsl(var(--primary)/0.6)]",
  outline: "border border-input bg-background/70 hover:bg-accent hover:text-accent-foreground",
  /** Solid in the theme's own contrast: dark on a light page, light on a dark one. */
  solid: "bg-foreground text-background hover:bg-foreground/85",
} as const;

/** The primary call to action: the app's default button look, as a link, with an arrow that nudges on hover. */
export function CtaLink({
  to,
  children,
  variant = "primary",
  arrow = false,
  className,
}: {
  to: string;
  children: ReactNode;
  variant?: keyof typeof CTA_STYLES;
  arrow?: boolean;
  className?: string;
}) {
  return (
    <Link to={to} className={cn(ctaBase, "group gap-2", CTA_STYLES[variant], className)}>
      {children}
      {arrow && (
        <ArrowRight
          aria-hidden="true"
          className="size-4 transition-transform group-hover:translate-x-0.5 motion-reduce:transition-none"
        />
      )}
    </Link>
  );
}

/** The same look for an in-page anchor. */
export function CtaAnchor({
  href,
  children,
  variant = "outline",
  className,
}: {
  href: string;
  children: ReactNode;
  variant?: keyof typeof CTA_STYLES;
  className?: string;
}) {
  return (
    <a href={href} className={cn(ctaBase, CTA_STYLES[variant], className)}>
      {children}
    </a>
  );
}
